import React from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Visibility as VisibilityIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import KanbanBoard from '../../common/KanbanBoard';
import {
  MAINTENANCE_STATUS_COLUMNS,
  resolveMaintenanceStatus,
  getMaintenanceStatusMeta,
  compareMaintenanceKanbanItems,
} from '../../../utils/maintenanceStatus';
import { canModifyMaintenanceRequest, canArchiveMaintenanceRequest } from '../../../utils/maintenancePermissions';

const PRIORITY_COLORS = {
  low: '#34D399',
  medium: '#818CF8',
  high: '#FCD34D',
  urgent: '#F87171',
};

const MaintenanceKanbanCard = ({ request, user, onOpen, onEdit, onArchive, draggable, isDragging }) => {
  const theme = useTheme();
  const statusMeta = getMaintenanceStatusMeta(resolveMaintenanceStatus(request));
  const priorityColor = PRIORITY_COLORS[request.priority] || '#94A3B8';

  return (
    <Box
      component={motion.div}
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      sx={{
        p: 1.5,
        mb: 1.25,
        borderRadius: '12px',
        cursor: draggable ? 'grab' : 'pointer',
        background: 'linear-gradient(160deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.9) 100%)',
        border: `1px solid ${alpha(statusMeta.color, 0.25)}`,
        boxShadow: isDragging
          ? `0 12px 32px ${alpha(statusMeta.color, 0.35)}`
          : '0 4px 16px rgba(0,0,0,0.25)',
        opacity: isDragging ? 0.85 : 1,
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          borderColor: alpha(statusMeta.color, 0.5),
          boxShadow: `0 8px 24px ${alpha(statusMeta.color, 0.2)}`,
        },
        '&:active': draggable ? { cursor: 'grabbing' } : undefined,
      }}
      onClick={() => onOpen(request)}
    >
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '0.9rem',
          color: '#fff',
          mb: 0.5,
          lineHeight: 1.3,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {request.title}
      </Typography>

      {request.location && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
          <LocationIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.4)' }} />
          <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.55)' }} noWrap>
            {request.location}
          </Typography>
        </Box>
      )}

      {request.description ? (
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: 'rgba(255,255,255,0.55)',
            mb: 1,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {request.description}
        </Typography>
      ) : null}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
        <Chip
          label={request.priority?.charAt(0).toUpperCase() + (request.priority?.slice(1) || 'Medium')}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.6rem',
            fontWeight: 700,
            bgcolor: alpha(priorityColor, 0.15),
            color: priorityColor,
            border: `1px solid ${alpha(priorityColor, 0.35)}`,
          }}
        />
        {request.type && (
          <Chip
            label={request.type.charAt(0).toUpperCase() + request.type.slice(1)}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.6rem',
              bgcolor: 'rgba(219,234,254,0.1)',
              color: 'rgba(255,255,255,0.65)',
              border: '1px solid rgba(148,163,184,0.25)',
            }}
          />
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <PersonIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.35)' }} />
          <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)' }} noWrap>
            {request.reported_by?.name || 'Unknown'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="View" arrow>
            <IconButton size="small" onClick={() => onOpen(request)} sx={{ color: theme.palette.info.main }}>
              <VisibilityIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {user && canModifyMaintenanceRequest(user, request) && (
            <>
              <Tooltip title="Edit" arrow>
                <IconButton
                  size="small"
                  onClick={() => onEdit(request)}
                  sx={{ color: '#f59e0b' }}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {onArchive && canArchiveMaintenanceRequest(user, request) && (
                <Tooltip title="Archive maintenance" arrow>
                  <IconButton size="small" onClick={() => onArchive(request)} sx={{ color: theme.palette.text.secondary }}>
                    <ArchiveIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      </Box>

      {request.reported_date && (
        <Typography
          sx={{
            mt: 0.75,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.62rem',
            color: 'rgba(255,255,255,0.35)',
          }}
        >
          {format(new Date(request.reported_date), 'MMM d, yyyy')}
        </Typography>
      )}
    </Box>
  );
};

const MaintenanceKanban = ({
  requests,
  user,
  canDrag,
  onStatusChange,
  onOpenRequest,
  onEditRequest,
  onArchiveRequest,
  updatingRequestId,
}) => (
  <KanbanBoard
    columns={MAINTENANCE_STATUS_COLUMNS}
    items={requests}
    getItemId={(r) => r.id}
    resolveColumnId={resolveMaintenanceStatus}
    sortItems={compareMaintenanceKanbanItems}
    canDragItem={(r) => canDrag(r)}
    onColumnChange={onStatusChange}
    updatingItemId={updatingRequestId}
    dragMimeType="application/x-maintenance-id"
    emptyMessage="Drop requests here"
    renderCard={(request, { isDragging, draggable }) => (
      <MaintenanceKanbanCard
        request={request}
        user={user}
        onOpen={onOpenRequest}
        onEdit={onEditRequest}
        onArchive={onArchiveRequest}
        draggable={draggable}
        isDragging={isDragging}
      />
    )}
  />
);

export default MaintenanceKanban;
